import { Box, Typography } from "@mui/material";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useContactCenterTheme } from "../../../virtual-receptionist/theming/ContactCenterThemeContext";
import { normalizeAudioSettingsItems } from "../helpers/normalizeAudioSettingsItems";
import { useAudioPreferences } from "../hooks/useAudioPreferences";
import { useAudioPreview } from "../hooks/useAudioPreview";
import { AudioSettingsItem } from "../types";
import { AudioSettingRow } from "./AudioSettingRow";

type AudioSettingsPanelProps = {
  channels: AudioSettingsItem[];
  title?: string;
  description?: string;
  hideHeader?: boolean;
};

export function AudioSettingsPanel({
  channels,
  title = "Audio settings",
  description = "Choose the sounds used by Contact Center alerts.",
  hideHeader = false,
}: AudioSettingsPanelProps) {
  const { theme } = useContactCenterTheme();
  const userState = useSelector((state: any) => state.userState);
  const items = useMemo(() => normalizeAudioSettingsItems(channels), [channels]);
  const [expandedChannel, setExpandedChannel] = useState<string | null>(null);
  const { preferences, updatePreference } = useAudioPreferences({
    items,
    companyId: userState?.activeCompanyId ?? userState?.company?.id,
    accountId: userState?.uid,
  });
  const { activePreview, preview, setPreviewVolume, stopPreview } = useAudioPreview();

  useEffect(() => {
    stopPreview();
  }, [expandedChannel, stopPreview]);

  if (items.length === 0) return null;

  return (
    <Box sx={{ px: 2, pt: 1.75, pb: 1.75 }}>
      {!hideHeader && (
        <Box sx={{ mb: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <VolumeUpRoundedIcon sx={{ fontSize: 13, color: theme.shell.textSecondary }} />
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: theme.shell.textSecondary,
              }}
            >
              {title}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: "0.72rem", color: theme.shell.textSecondary }}>
            {description}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 1 }}>
        {items.map((item) => (
          <AudioSettingRow
            key={`${item.scope}-${item.channel}`}
            item={item}
            preference={preferences[item.channel]}
            expanded={expandedChannel === item.channel}
            onToggle={() => setExpandedChannel((current) => (current === item.channel ? null : item.channel))}
            onToneChange={(toneId) => updatePreference(item.channel, { toneId })}
            onVolumeChange={(volume) => {
              updatePreference(item.channel, { volume });
              setPreviewVolume(item.channel, volume / 100);
            }}
            onPreview={(value) => preview(item.channel, value, (preferences[item.channel]?.volume ?? 80) / 100)}
            activePreviewToneId={activePreview?.channel === item.channel ? activePreview.toneId : null}
          />
        ))}
      </Box>
    </Box>
  );
}
